import { Controller } from "stimulus";
import dialogPolyfill from 'dialog-polyfill'
import { formState } from 'yuzu-plugin-forms';

export default class extends Controller {

  static get targets() {
    return [
      'modalContent'
    ];
  }

  initialize() {
    dialogPolyfill.registerDialog(this.element);
    this.setupVars();
    this.setupOtherModalClose();
    this.setupButtonClickEvents();
    this.setupScrollEvent();
    this.checkTimeouts();
    this.checkOpenParameter();
  }

  setupVars() {
    const data = this.element.dataset;

    this.openModalParamName = 'openModal';

    this.modalId = data.modal;
    this.dismissCount = data.dismissCount ? parseFloat(data.dismissCount) : Infinity;
    this.revealSeconds = data.revealSeconds ? parseInt(data.revealSeconds) : undefined;
    this.scrollRevealPercentage = data.scrollRevealPercentage ? parseInt(data.scrollRevealPercentage) : undefined;
    this.storageType = data.storageType ? data.storageType : 'session';

    this.isTimeoutModal = this.revealSeconds !== undefined;
    this.isScrollModal = this.scrollRevealPercentage !== undefined;

    this.storage = this.getStorageType(this.storageType);

    this.revealTimeout;
  }

  setupOtherModalClose() {
    this.otherModalCloseEvent = 'Modal:Close';
    this.boundOtherModalClose = this.onOtherModalClose.bind(this);
  }

  setupButtonClickEvents() {
    this.modalTriggerTargets = document.querySelectorAll(`[data-modal-id="${this.modalId}"]`);

    this.modalTriggerTargets.forEach(element => {
      element.addEventListener('click', (e) => {
        if (e.currentTarget.tagName === 'A') {
          e.preventDefault();
        }
        this.toggleModal();
      });
    });
  }

  setupScrollEvent() {
    this.scrollTimeout;
    const debounceTimeout = 50;

    if (this.scrollRevealPercentage === undefined || this.isDismissCountExceeded()) {
      return;
    }

    window.addEventListener('scroll', () => {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        this.checkScrollPosition();
      }, debounceTimeout);
    });
  }

  checkScrollPosition() {
    const pageHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const windowHeight = window.innerHeight;
    const scrollPosition = window.scrollY + windowHeight;
    const scrollPercentage = (scrollPosition / pageHeight) * 100;
    const isPercentageReached = scrollPercentage >= this.scrollRevealPercentage;

    if (isPercentageReached) {
      const showAllowed = !this.isDismissCountExceeded();
      const isOpen = this.element.hasAttribute('open');

      if (isOpen) {
        return;
      }
      if (showAllowed && this.isTimeoutModal) {
        this.checkTimeouts();
      }
      else if (showAllowed) {
        this.toggleModal();
      }
    }
    return isPercentageReached;
  }

  checkOpenParameter() {
    const queryString = window.location.search;
    const modalId = this.element.dataset.modal;
    const params = new URLSearchParams(queryString);

    if (params.has(this.openModalParamName) && params.get(this.openModalParamName) === modalId) {
      this.toggleModal();
    }
  }

  getStorageType(storageType = this.storageType) {
    return storageType === 'local' ? localStorage : sessionStorage;
  }

  getStorageItem(key) {
    return this.storage.getItem(`modal-${this.modalId}-${key}`);
  }

  setStorageItem(key, val) {
    this.storage.setItem(`modal-${this.modalId}-${key}`, val);
  }

  removeStorageItem(key) {
    this.storage.removeItem(`modal-${this.modalId}-${key}`);
  }

  toggleModal() {
    const isModalOpen = this.isModalOpen(this.element);
    if (!isModalOpen && !this.isOtherModalsOpen()) {
      formState.addHeader('yuzu-modal-id', this.modalId);
      this.element.showModal();
      document.removeEventListener(this.otherModalCloseEvent, this.boundOtherModalClose);
    }
    else if (isModalOpen) {
      this.closeModal();
      document.removeEventListener(this.otherModalCloseEvent, this.boundOtherModalClose);
    }
    else {
      document.addEventListener(this.otherModalCloseEvent, this.boundOtherModalClose);
    }
  }

  isModalOpen(modal) {
    return modal.hasAttribute('open');
  }

  closeModal(e) {
    this.element.close();
    this.incrementDismissCounter();

    if (e) {
      e.stopPropagation();
    }

    if (this.isTimeoutModal) {
      this.onTimeoutModalClose();
    }
    let closeEvent = new Event(this.otherModalCloseEvent, { bubbles: true });
    document.dispatchEvent(closeEvent);
  }

  onOtherModalClose() {
    this.toggleModal();
  }

  backdropDismiss(e) {
    if (!e.currentTarget.isEqualNode(e.target)) {
      return;
    }

    const rect = this.modalContentTarget.getBoundingClientRect();
    if (e.clientY < rect.top || e.clientY > rect.bottom ||
      e.clientX < rect.left || e.clientX > rect.right) {
      this.closeModal();
    }
  }

  checkTimeouts() {
    if (this.isDismissCountExceeded() || !this.isTimeoutModal) {
      if (this.revealTimeout) {
        clearTimeout(this.revealTimeout);
      }
      return;
    }
    let now = new Date();
    let revealDelaySeconds = 0;
    let modalShowAfter = this.getStorageItem(`revealAfter`);

    if (modalShowAfter === null) {
      // console.info('The user has not seen the popup before. This looks like a first visit.');    
      let revealDateTime = new Date(now.getTime() + (this.revealSeconds * 1000));
      this.setStorageItem(`revealAfter`, revealDateTime);
      revealDelaySeconds = this.revealSeconds;

      // console.info('Show the popup after '+ this.revealSeconds +' seconds. This will be after ' + popAtDate);
    }
    else {
      let storageDate = new Date(modalShowAfter);
      let secondsDiffExisting = storageDate - now;

      // console.info('The user been on the site before. We see a date the popup was set to show.');
      // console.info('The date to show after was previously set at:', storageDate, '. This is in ', secondsDiffExisting / 1000, ' seconds. );
      revealDelaySeconds = secondsDiffExisting / 1000;
    }

    // console.info('SHOW IT in (seconds)', revealDelaySeconds);
    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout);
    }
    this.revealTimeout = setTimeout(() => {
      if (!this.isScrollModal || this.isScrollModal && this.checkScrollPosition()) {
        this.showTimeoutModal();
      }
    }, revealDelaySeconds * 1000);
  }

  showTimeoutModal() {
    let modalShowAfterDate = Date.parse(this.getStorageItem(`revealAfter`));
    // console.info('show automatic modal function called', modalShowAfterDate);
    let now = new Date();
    if (now > modalShowAfterDate) {
      this.toggleModal();
    } else {
      // console.info('pop date has changed! Dont show');
    }
  }

  onTimeoutModalClose() {
    let now = new Date();

    if (this.isDismissCountExceeded()) {
      return;
    }
    let revealDelayMilliseconds = this.revealSeconds * 1000;
    let revealDate = new Date(now.getTime() + revealDelayMilliseconds);
    this.setStorageItem(`revealAfter`, revealDate);
    this.checkTimeouts();
  }

  incrementDismissCounter() {
    let dismissedCount = this.getStorageItem('dismissCount') ? parseInt(this.getStorageItem('dismissCount')) : 0;
    this.setStorageItem('dismissCount', dismissedCount + 1);
  }

  isDismissCountExceeded() {
    const dismissedCount = this.getStorageItem('dismissCount') ? parseInt(this.getStorageItem('dismissCount')) : 0;
    return dismissedCount >= this.dismissCount;
  }

  isOtherModalsOpen() {
    const modals = this.getOtherModals();
    const filteredModals = modals.filter(modal => {
      return this.isModalOpen(modal);
    })
    return filteredModals.length > 0
  }

  getOtherModals() {
    const descendants = this.element.parentNode.children;
    const element = this.element;
    return Array.prototype.filter.call(descendants, function (sibling) {
      return sibling !== element;
    });
  }
}